import { AlertCircle, AlertTriangle } from 'lucide-preact'
import type { IntegrationErrorEntryDto } from '@/types/integrations'
import { timeFromNowVerbose } from '@/utils/time'
import { useErrorSummary } from './useErrorSummary'
import { ErrorSummarySection } from './ErrorSummarySection'

function truncateMessage(msg: string, max = 120): string {
  return msg.length > max ? msg.slice(0, max) + '…' : msg
}

interface IntegrationErrorDetailProps {
  // Health state, normalized so both the standard panel (PascalCase view DTO) and the
  // HubSpot status DTO (camelCase top-level, PascalCase error entries) can feed it.
  status?: string | null
  isSoftFailure?: boolean
  consecutiveFailureCount?: number
  recentErrors?: IntegrationErrorEntryDto[]
  // Cached AI summary from the list/status response, used as the fetch seed.
  aiSummary?: string | null
  clientCompanyId?: string | null
  // When absent (e.g. the HubSpot status DTO surfaces no connection id), the on-demand
  // error-summary fetch is skipped and only a seeded `aiSummary` renders.
  connectionId?: string
  onSummaryFetched?: (summary: string) => void
}

/**
 * The "Recent Errors" block: an error/warning heading, the consecutive-failure count, the
 * AI summary card, and the deduplicated recent-error list. Shared by the standard
 * integration panel and the HubSpot summary so both surface health identically.
 */
export function IntegrationErrorDetail({
  status,
  isSoftFailure,
  consecutiveFailureCount,
  recentErrors,
  aiSummary,
  clientCompanyId,
  connectionId,
  onSummaryFetched,
}: IntegrationErrorDetailProps) {
  const summary = useErrorSummary(clientCompanyId, connectionId, aiSummary, onSummaryFetched)
  const errors = recentErrors || []
  const isError = status === 'Error'
  const isWarning = !isError && isSoftFailure === true
  // Render the summary slot when we can fetch (both ids) or already have a seed to show.
  const showSummary = (!!clientCompanyId && !!connectionId) || !!aiSummary

  return (
    <div className="space-y-3">
      <h5 className="text-slate-700 dark:text-slate-200 font-semibold text-sm flex items-center gap-2">
        {isError ? (
          <AlertCircle className="w-4 h-4 text-red-500" />
        ) : isWarning ? (
          <AlertTriangle className="w-4 h-4 text-amber-500" />
        ) : null}
        Recent Errors
      </h5>

      {(consecutiveFailureCount ?? 0) > 0 && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {consecutiveFailureCount} consecutive failures since last successful sync
        </p>
      )}

      {showSummary && (
        <div>
          <ErrorSummarySection summary={summary} />
        </div>
      )}

      <ul className="space-y-2">
        {errors.map((err, idx) => (
          <li key={idx} className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-3 text-xs">
            <p className="text-slate-800 dark:text-slate-200 leading-relaxed break-words">{truncateMessage(err.Message)}</p>
            <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500 dark:text-slate-400">
              {err.Count > 1 && <span>(seen {err.Count} times)</span>}
              <span>Last seen {timeFromNowVerbose(err.LastSeenUtc)}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
